import { useMutation, useQuery } from '@apollo/client/react';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Linking, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Icon } from 'react-native-paper';

import { AppbarBackButton, AppbarTitle, ScreenAppbar } from '@/components/atoms/appbar';
import { InlineBanner } from '@/components/atoms/banner';
import { OutlineButton, PrimaryButton } from '@/components/atoms/button';
import { ConfirmDialog } from '@/components/atoms/dialog';
import { ActionFooterBar } from '@/components/atoms/footer';
import { ErrorState, LoadingState } from '@/components/atoms/state';
import {
  HandoffActivityFilterChips,
  HandoffDetailTabs,
  type HandoffActivityFilter,
  type HandoffDetailTab,
} from '@/components/molecules/ai-baton';
import { HandoffActivityHistory, HandoffCustomerInfo } from '@/components/organisms/ai-baton';
import { formatDateOnly } from '@/formatters/formatDateOnly';
import { formatTimeOnly } from '@/formatters/formatTimeOnly';
import {
  ClaimHandoffDocument,
  GetContactDocument,
  GetContactStatusesDocument,
  GetHandoffDocument,
  GetHandoffUnreadStateDocument,
  MeDocument,
  ReleaseHandoffDocument,
  UpdateNotificationDispatchDocument,
} from '@/generated/usermobile-bff/graphql';
import { useTranslation } from '@/i18n';
import {
  HANDOFF_SEVERITY_TRANSLATION_KEYS,
  HANDOFF_TYPE_DETAIL_TITLE_KEYS,
  getContactPhoneNumber,
  getHandoffAction,
  getHandoffAssignmentKey,
  getHandoffDescription,
  getHandoffPrimaryAction,
  isHandoffAssignedToCurrentUser,
  isHandoffClaimable,
  type HandoffAction,
} from '@/models/handoffs';
import { parseContactIdParam } from '@/models/handoffRouteParams';
import { useGlobalAlertStore } from '@/stores/useGlobalAlertStore';
import { colorSemantic, spacing, unisonColors, useAppTheme } from '@/theme';

const STATUS_DOT_SIZE = 8;

interface HandoffDetailProps {
  readonly id: string;
}

export const HandoffDetail = ({ id }: HandoffDetailProps) => {
  const { t } = useTranslation();
  const { colors } = useAppTheme();
  const router = useRouter();
  const showGlobalAlert = useGlobalAlertStore((state) => state.showGlobalAlert);

  const scrollRef = useRef<ScrollView>(null);

  const [activeTab, setActiveTab] = useState<HandoffDetailTab>('activity');
  const [activityFilter, setActivityFilter] = useState<HandoffActivityFilter>('sms');
  const [callConfirmVisible, setCallConfirmVisible] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { data: meData, loading: meLoading } = useQuery(MeDocument);
  const currentUserUid = meData?.me.backendAppUserUid;

  const { data, loading } = useQuery(GetHandoffDocument, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
  });

  const handoff = data?.handoff;
  const contactId = handoff?.contactId ?? null;
  const numericContactId = parseContactIdParam(contactId);

  const { data: contactData } = useQuery(GetContactDocument, {
    variables: { id: numericContactId ?? 0 },
    fetchPolicy: 'cache-and-network',
    skip: numericContactId == null,
  });

  const { data: statusesData } = useQuery(GetContactStatusesDocument, {
    fetchPolicy: 'cache-first',
    skip: numericContactId == null,
  });

  const contact = contactData?.contact ?? null;

  const contactStatus = useMemo(() => {
    if (contact?.contactStatusId == null) {
      return null;
    }

    return (
      (statusesData?.contactStatuses.data ?? []).find(
        (status) => status.id === contact.contactStatusId,
      ) ?? null
    );
  }, [contact?.contactStatusId, statusesData?.contactStatuses.data]);

  const description = useMemo(
    () =>
      handoff == null
        ? null
        : getHandoffDescription({
            handoffType: handoff.handoffType,
            notes: handoff.notes,
            contactName: contact?.fullName,
            t,
          }),
    [contact?.fullName, handoff, t],
  );

  const [claimHandoff] = useMutation(ClaimHandoffDocument);
  const [releaseHandoff] = useMutation(ReleaseHandoffDocument);
  const [updateNotificationDispatch] = useMutation(UpdateNotificationDispatchDocument);

  // Opening a handoff marks its dispatch read: the red dots and the bottom-bar
  // badge clear. The mutation returns no entity, so the cached field is nulled
  // explicitly and the unread state query refetches.
  const unreadDispatchId = handoff?.unreadDispatch?.id ?? null;
  const markedReadDispatchId = useRef<string | null>(null);

  useEffect(() => {
    if (unreadDispatchId == null || markedReadDispatchId.current === unreadDispatchId) {
      return;
    }

    markedReadDispatchId.current = unreadDispatchId;

    void updateNotificationDispatch({
      variables: { input: { userDispatchId: unreadDispatchId, status: 'READ' } },
      refetchQueries: [{ query: GetHandoffUnreadStateDocument }],
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'Handoff', id }),
          fields: { unreadDispatch: () => null },
        });
      },
    });
  }, [id, unreadDispatchId, updateNotificationDispatch]);

  const isAssignedToMe =
    handoff != null &&
    isHandoffAssignedToCurrentUser({
      status: handoff.status,
      userUid: handoff.userUid,
      currentUserUid,
    });

  const subtitle = useMemo(() => {
    if (handoff == null) {
      return undefined;
    }

    const assignmentKey = getHandoffAssignmentKey({
      status: handoff.status,
      userUid: handoff.userUid,
      currentUserUid,
    });

    const severityKey = HANDOFF_SEVERITY_TRANSLATION_KEYS[handoff.severity];
    const createdAtLabel = `${formatDateOnly(handoff.createdAt)} ${formatTimeOnly(handoff.createdAt)}`;

    const parts = [t(assignmentKey), severityKey ? t(severityKey) : null, createdAtLabel];

    return parts.filter((part) => part != null).join(' - ');
  }, [currentUserUid, handoff, t]);

  const runHandoffMutation = useCallback(
    async (action: HandoffAction) => {
      setIsUpdating(true);

      const mutate = action === 'claim' ? claimHandoff : releaseHandoff;

      try {
        await mutate({ variables: { input: { handoffId: id } } });
      } catch {
        showGlobalAlert({ text: t(`aiTasks.error.${action}Failed`), variant: 'error' });
      } finally {
        setIsUpdating(false);
      }
    },
    [claimHandoff, id, releaseHandoff, showGlobalAlert, t],
  );

  const primaryAction = handoff ? getHandoffPrimaryAction(handoff.handoffType) : null;
  const claimAction = handoff ? getHandoffAction({ status: handoff.status }) : null;
  const phoneNumber = contact ? getContactPhoneNumber(contact) : null;

  const handlePrimaryPress = useCallback(() => {
    if (primaryAction === 'sms') {
      router.push(`/handoff/${id}/sms`);

      return;
    }

    if (phoneNumber == null) {
      showGlobalAlert({ text: t('aiTasks.detail.noPhoneNumber'), variant: 'error' });

      return;
    }

    setCallConfirmVisible(true);
  }, [id, phoneNumber, primaryAction, router, showGlobalAlert, t]);

  // Deferred a frame because the bubble that triggered this has not been laid out yet
  // when the effect runs, so scrolling immediately stops short of it.
  const handleMessagesRendered = useCallback(() => {
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        scrollRef.current?.scrollToEnd({ animated: false });
      });
    }
  }, []);

  const handleConfirmCall = useCallback(async () => {
    setCallConfirmVisible(false);

    if (phoneNumber == null) {
      return;
    }

    // The iOS simulator has no phone app, so openURL rejects there instead of dialling.
    try {
      await Linking.openURL(`tel:${phoneNumber.replace(/[^\d+]/g, '')}`);
    } catch {
      showGlobalAlert({ text: t('aiTasks.detail.callFailed'), variant: 'error' });
    }
  }, [phoneNumber, showGlobalAlert, t]);

  if ((loading || meLoading) && !handoff) {
    return <LoadingState />;
  }

  // Covers both a failed request and a request that came back without a handoff.
  if (!handoff) {
    return <ErrorState message={t('aiTasks.detail.loadError')} />;
  }

  const showWarningBanner = isHandoffClaimable({ status: handoff.status });
  const primaryLabel =
    primaryAction === 'call' ? t('aiTasks.detail.call') : t('aiTasks.detail.createSms');

  // Report only handoffs carry no way to reach the customer, so handing the baton
  // back is the primary action there.
  const renderBatonAction = () => {
    if (claimAction === null) {
      return null;
    }

    const label = t(`aiTasks.action.${claimAction}`);
    const onPress = () => void runHandoffMutation(claimAction);

    if (primaryAction === 'none') {
      return (
        <PrimaryButton disabled={isUpdating} testID="handoff-detail-claim-action" onPress={onPress}>
          {label}
        </PrimaryButton>
      );
    }

    return (
      <OutlineButton disabled={isUpdating} testID="handoff-detail-claim-action" onPress={onPress}>
        {label}
      </OutlineButton>
    );
  };

  return (
    <View style={styles.container}>
      <ScreenAppbar testID="handoff-detail-appbar" variant="dark">
        <AppbarBackButton
          testID="handoff-detail-back-button"
          variant="dark"
          onPress={() => router.back()}
        />
        <AppbarTitle
          subtitle={subtitle}
          testID="handoff-detail-title"
          title={t(HANDOFF_TYPE_DETAIL_TITLE_KEYS[handoff.handoffType])}
          variant="dark"
        />
      </ScreenAppbar>
      {showWarningBanner ? (
        <InlineBanner
          message={t('aiTasks.detail.notClaimedWarning')}
          testID="handoff-detail-warning-banner"
          variant="warning"
        />
      ) : null}
      <View style={styles.summary}>
        <View style={styles.statusRow}>
          {contactStatus?.color ? (
            <View
              style={[styles.statusDot, { backgroundColor: contactStatus.color }]}
              testID="handoff-detail-status-dot"
            />
          ) : (
            <View testID="handoff-detail-status-none">
              <Icon
                color={colorSemantic.colorIconNdDisabled}
                size={STATUS_DOT_SIZE + spacing[1]}
                source="circle-off-outline"
              />
            </View>
          )}
          <Text style={[styles.statusLabel, { color: colors.textNdPrimary }]}>
            {t('aiTasks.detail.statusLabel', {
              status: contactStatus?.name ?? t('aiTasks.detail.noStatus'),
            })}
          </Text>
        </View>
        {description ? (
          <Text style={[styles.notes, { color: colors.textNdPrimary }]}>{description}</Text>
        ) : null}
      </View>
      <HandoffDetailTabs
        activeTab={activeTab}
        activityLabel={t('aiTasks.detail.tab.activity')}
        customerLabel={t('aiTasks.detail.tab.customer')}
        onSelect={setActiveTab}
      />
      {activeTab === 'activity' ? (
        <View style={styles.filterRow}>
          <HandoffActivityFilterChips
            activeFilter={activityFilter}
            correspondenceLabel={t('aiTasks.detail.chip.correspondence')}
            smsLabel={t('aiTasks.detail.chip.sms')}
            onSelect={setActivityFilter}
          />
        </View>
      ) : null}
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={styles.scrollContent}
        style={styles.scroll}
        testID="handoff-detail-scroll">
        {activeTab === 'activity' ? (
          <HandoffActivityHistory
            contactId={contactId}
            contactName={contact?.fullName ?? null}
            filter={activityFilter}
            onMessagesRendered={handleMessagesRendered}
          />
        ) : (
          <HandoffCustomerInfo contact={contact} />
        )}
      </ScrollView>
      <ActionFooterBar testID="handoff-detail-footer">
        <OutlineButton testID="handoff-detail-close" onPress={() => router.back()}>
          {t('aiTasks.detail.close')}
        </OutlineButton>
        {renderBatonAction()}
        {primaryAction === 'none' ? null : (
          <PrimaryButton
            disabled={!isAssignedToMe || isUpdating}
            testID="handoff-detail-primary-action"
            onPress={handlePrimaryPress}>
            {primaryLabel}
          </PrimaryButton>
        )}
      </ActionFooterBar>
      <ConfirmDialog
        body={t('aiTasks.detail.callConfirm.body', { name: contact?.fullName ?? '' })}
        confirmLabel={t('aiTasks.detail.callConfirm.confirm')}
        dismissLabel={t('aiTasks.detail.callConfirm.dismiss')}
        testID="handoff-detail-call-confirm"
        title={t('aiTasks.detail.callConfirm.title')}
        visible={callConfirmVisible}
        onConfirm={() => void handleConfirmCall()}
        onDismiss={() => setCallConfirmVisible(false)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: unisonColors.bgNlWeak,
    flex: 1,
  },
  filterRow: {
    paddingHorizontal: spacing[4],
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: spacing[4],
  },
  summary: {
    backgroundColor: unisonColors.white,
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[3],
    rowGap: spacing[2],
  },
  statusRow: {
    alignItems: 'center',
    columnGap: spacing[2],
    flexDirection: 'row',
  },
  statusDot: {
    borderRadius: STATUS_DOT_SIZE / 2,
    height: STATUS_DOT_SIZE,
    width: STATUS_DOT_SIZE,
  },
  statusLabel: {
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  notes: {
    fontSize: 14,
    lineHeight: 22,
  },
});
